import { useCallback, useState } from 'react'

import {
  countErrorsPerTab,
  errorsOfField,
  firstTabWithErrors,
  ITabDef,
  unmappedErrorMessages,
  withoutFieldErrors
} from './tabErrorUtils'
import { useAppToast } from './useAppToast'

/**
 * Field errors of a form split in tabs (a `FormModal` with `tabs`): the errors, the open tab and
 * the error count of each tab. `showErrors` opens the first tab holding an error and warns with
 * `toast.invalidForm`; errors no tab can show go in the toast's description.
 */
export function useTabFormErrors(tabs: ITabDef[]) {
  const toast = useAppToast()
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [tabIndex, setTabIndex] = useState(0)

  /** Drops the errors of the given fields (and of their items) once they are edited. */
  const clearErrors = useCallback((fields: string[]) => setErrors((prev) => withoutFieldErrors(prev, fields)), [])

  const showErrors = (next: Record<string, string[]>) => {
    setErrors(next)
    const offendingTab = firstTabWithErrors(tabs, next)
    setTabIndex(offendingTab >= 0 ? offendingTab : 0)
    toast.invalidForm(offendingTab >= 0 ? undefined : unmappedErrorMessages(tabs, next).join(' '))
  }

  return {
    errors,
    setErrors,
    tabIndex,
    setTabIndex,
    errorCounts: countErrorsPerTab(tabs, errors),
    clearErrors,
    /** The field's own message plus any per-item one (`duties.3`). */
    listErrors: (field: string) => errorsOfField(errors, field),
    showErrors
  }
}
